import { useToast } from "../../../components/ui";
import { useAccessLapse } from "./useAccessLapse";

/**
 * What a workspace with no plan running may not do.
 *
 * Clients, enquiries and meal plans are what the product is sold for, so
 * nothing new can be added to any of the three until there is a subscription
 * behind them: the create buttons are off, while the screens themselves stay
 * readable. One hook rather than the same lines on each screen: the pages must
 * agree about when the door is shut, and about what they say to someone
 * standing at it.
 *
 * This is the browser's half of the rule and nothing more. The server is what
 * actually refuses — a locked button is a courtesy, not a lock, and typing the
 * URL of the Add client form still reaches it.
 */

/** Why the create buttons are off, in the dietitian's terms. */
export const NEEDS_PLAN =
  "Your subscription is not running, so nothing new can be added to this " +
  "workspace yet. Pick a plan under Settings › Billings and it unlocks " +
  "straight away.";

/**
 * The same door, for the workspace that has just spent its fortnight.
 *
 * A different answer because it is a different fact: this is not somebody who
 * never had access, it is somebody who had it until today and is likely to be
 * pressing the button out of habit. Naming the trial is what makes the greyed
 * button make sense — "you need a plan" reads as a mistake to a dietitian who
 * was adding clients yesterday.
 */
export const TRIAL_ENDED_HEADING = "Trial ended";
export const TRIAL_ENDED =
  "Your free trial has ended. Upgrade to a paid plan to continue using this " +
  "feature.";

/** A locked button's two lines: the heading, and the note under it. */
export interface GateReason {
  heading: string;
  detail: string;
}

/**
 * What a shut door says, which depends on how it came to be shut.
 *
 * Exported because the dashboard works its own lock out — it already holds the
 * billing answer and must not ask for it twice — and the words a button gives
 * must not depend on which screen it happens to be standing on.
 *
 * `thing` is the action in the infinitive — "add a client", "create a plan" —
 * and is only used by the answer that names it; the trial's own line is about
 * the workspace rather than the button, and reads the same wherever it is
 * raised.
 */
export const gateReason = (thing: string, trialEnded: boolean): GateReason =>
  trialEnded
    ? { heading: TRIAL_ENDED_HEADING, detail: TRIAL_ENDED }
    : { heading: `You need a plan to ${thing}`, detail: NEEDS_PLAN };

/** What a gated button carries — spread straight onto it. */
export interface GateProps {
  disabled: boolean;
  /** The reason, for the pointer that rests on it. */
  title?: string;
  /** The reason again, out loud, for the press that comes anyway. */
  onDisabledClick?: () => void;
}

/**
 * The same courtesy, for a control that is not a `Button`.
 *
 * `Button` takes `GateProps` as they stand: it knows to drop the native
 * `disabled` attribute when there is a reason to give, since the browser
 * dispatches nothing to a natively disabled button and the press that would ask
 * why never lands. A bare `<button>` — a pencil on a pill, a tick in a table
 * cell, the text action beside a status — does not know that, so this spells it
 * out: marked disabled to a screen reader, the reason under the pointer, and
 * the press turned back to the reason rather than to the action.
 *
 * The greyed look is left to the caller. Every one of these controls is drawn
 * differently, and each already has its own way of going quiet.
 */
export const lockedProps = (gate: GateProps, onPress: () => void) => ({
  "aria-disabled": gate.disabled ? (true as const) : undefined,
  title: gate.title,
  onClick: gate.disabled ? gate.onDisabledClick ?? (() => {}) : onPress,
});

export interface SubscriptionGate {
  /**
   * Whether the product is shut.
   *
   * True only once the billing read has answered and said there is neither a
   * plan running nor a trial. A read that is still out, or one that failed,
   * leaves everything open: not knowing is not the same as knowing they have
   * not paid, and a paying dietitian must never find their own workspace shut
   * because a call was slow. The same caution the rail takes over the plan name
   * under the account, and the dashboard over its checklist.
   */
  locked: boolean;
  /**
   * What a button that does `thing` — "add a client", "create a plan" — has to
   * wear. Empty where nothing is locked, so it can be spread unconditionally.
   *
   * Still worth carrying on a screen that is behind the modal: the buttons are
   * drawn underneath it, and the modal is gone the moment a plan is bought.
   */
  props: (thing: string) => GateProps;
}

/**
 * Must be called inside a `<ToastProvider>`: a button that has gone grey owes
 * an answer to the press that did not know why, and the toast is where that is
 * said. See `Button`'s `onDisabledClick`.
 */
export function useSubscriptionGate(): SubscriptionGate {
  /**
   * One fact decides the lock: `access_source`, which the server answers and
   * this app never second-guesses — see `hasAccess`. What decides the *words*
   * is which of the two ran out, and that takes a second read; both are
   * `useAccessLapse`'s, so a greyed button and the dashboard's strip cannot end
   * up naming different things.
   *
   * The trial's line is for the workspace whose only access was ever the trial.
   * A lapsed plan, and anything this app cannot tell apart, gets `NEEDS_PLAN` —
   * true of a practice between terms as much as of a first purchase.
   */
  const { locked, lapse } = useAccessLapse();
  const toast = useToast();

  const trialEnded = lapse === "trial";

  return {
    locked,
    props: (thing: string) => {
      if (!locked) return { disabled: false };

      const { heading, detail } = gateReason(thing, trialEnded);
      return {
        disabled: true,
        title: heading,
        onDisabledClick: () => toast.warning(heading, detail),
      };
    },
  };
}
